import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Paperclip, X, Eye, FileText, Image as ImageIcon } from 'lucide-react';

const MAX_FILES = 15;
const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
  'image/heic',
  'image/heif',
]);

const EXT_TO_TYPE = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  pdf: 'application/pdf',
  heic: 'image/heic',
  heif: 'image/heif',
};

function normalizeType(t) {
  if (!t) return '';
  const c = t.split(';')[0].trim().toLowerCase();
  if (c === 'image/jpg') return 'image/jpeg';
  return c;
}

function resolveContentType(file) {
  const fromMime = normalizeType(file.type);
  if (ALLOWED_TYPES.has(fromMime)) return fromMime;
  const ext = (file.name || '').split('.').pop()?.toLowerCase() || '';
  return EXT_TO_TYPE[ext] || '';
}

function isHeicType(ct) {
  return ct === 'image/heic' || ct === 'image/heif';
}

/**
 * Local file picker + list + modal preview (blob URLs) for order create/edit flows.
 * labels: { title, hint, browse, previewTitle, remove, maxFiles, fileTooLarge, invalidType, fieldId? }
 */
export default function OrderServiceDocumentsField({
  files,
  onFilesChange,
  disabled,
  labels,
  onValidationError,
}) {
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    return () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    };
  }, [preview]);

  const fieldId = labels.fieldId || 'default';

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;
    const next = [...files];
    let err = '';
    for (const f of picked) {
      if (next.length >= MAX_FILES) {
        err = labels.maxFiles;
        break;
      }
      const ct = resolveContentType(f);
      if (!ct || !ALLOWED_TYPES.has(ct)) {
        err = labels.invalidType;
        continue;
      }
      if (f.size > MAX_BYTES) {
        err = labels.fileTooLarge;
        continue;
      }
      next.push(f);
    }
    onFilesChange(next);
    if (err && onValidationError) onValidationError(err);
  };

  const removeAt = (i) => {
    onFilesChange(files.filter((_, j) => j !== i));
  };

  const openPreview = (file) => {
    const ct = resolveContentType(file);
    if (isHeicType(ct)) {
      if (onValidationError && labels.previewUnavailableHeic) {
        onValidationError(labels.previewUnavailableHeic);
      }
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return {
        url,
        name: file.name,
        isPdf: ct === 'application/pdf',
      };
    });
  };

  const closePreview = () => {
    setPreview((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  return (
    <div className="space-y-2 rounded-lg border border-dashed border-gray-300 bg-gray-50/80 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Paperclip className="h-4 w-4 text-gray-600" />
          <Label className="text-sm font-semibold">{labels.title}</Label>
        </div>
        <div>
          <input
            type="file"
            id={`order-docs-${fieldId}`}
            className="hidden"
            accept=".pdf,.heic,.heif,image/jpeg,image/png,image/webp,image/heic,image/heif"
            multiple
            disabled={disabled}
            onChange={onPick}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || files.length >= MAX_FILES}
            onClick={() => document.getElementById(`order-docs-${fieldId}`)?.click()}
          >
            {labels.browse}
          </Button>
        </div>
      </div>
      <p className="text-xs text-gray-500">{labels.hint}</p>
      {files.length > 0 && (
        <ul className="mt-2 space-y-2">
          {files.map((f, i) => {
            const ct = resolveContentType(f);
            const isPdf = ct === 'application/pdf';
            const isHeic = isHeicType(ct);
            return (
              <li
                key={`${f.name}-${i}-${f.size}`}
                className="flex items-center justify-between gap-2 rounded-md border bg-white px-2 py-1.5 text-sm"
              >
                <div className="flex min-w-0 items-center gap-2">
                  {isPdf ? (
                    <FileText className="h-4 w-4 shrink-0 text-red-600" />
                  ) : (
                    <ImageIcon className="h-4 w-4 shrink-0 text-blue-600" />
                  )}
                  <span className="truncate text-gray-800" title={f.name}>
                    {f.name}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2"
                    onClick={() => openPreview(f)}
                    disabled={disabled || isHeic}
                    title={isHeic ? labels.previewUnavailableHeic : undefined}
                  >
                    <Eye className="h-4 w-4" />
                    <span className="sr-only">{labels.previewTitle}</span>
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-red-600"
                    onClick={() => removeAt(i)}
                    disabled={disabled}
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">{labels.remove}</span>
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Dialog
        open={!!preview}
        onOpenChange={(open) => {
          if (!open) closePreview();
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="truncate pr-8">{preview?.name || labels.previewTitle}</DialogTitle>
          </DialogHeader>
          {preview?.isPdf ? (
            <iframe title={preview.name} src={preview.url} className="min-h-[70vh] w-full flex-1 rounded border" />
          ) : preview?.url ? (
            <div className="flex max-h-[70vh] justify-center overflow-auto">
              <img src={preview.url} alt={preview.name} className="max-h-[70vh] max-w-full object-contain" />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Fire-and-forget: logs on the API as `order_service_document_upload_failed` so support can grep logs.
 */
export async function reportOrderServiceDocumentUploadFailure({
  backendUrl,
  token,
  orderId,
  httpStatus,
  message,
  filenames,
  isAdminPath,
  source,
}) {
  if (!orderId || !token || !backendUrl) return;
  try {
    await fetch(`${backendUrl}/api/orders/${orderId}/service-document-upload-failures`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        http_status: httpStatus,
        message: String(message || '').slice(0, 2000),
        filenames: (filenames || []).slice(0, 20).map((n) => String(n).slice(0, 180)),
        is_admin_path: !!isAdminPath,
        source: String(source || 'order_documents').slice(0, 32),
      }),
    });
  } catch (_) {
    /* ignore */
  }
}

export async function uploadOrderServiceDocuments(orderId, fileList, backendUrl, token, isAdmin) {
  if (!fileList || !fileList.length || !token) return;
  const path = isAdmin
    ? `${backendUrl}/api/admin/orders/${orderId}/service-documents`
    : `${backendUrl}/api/orders/${orderId}/service-documents`;
  for (const file of fileList) {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(path, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) {
      let detail = 'Upload failed';
      try {
        const j = await res.json();
        if (j.detail) detail = typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail);
      } catch (_) {
        /* ignore */
      }
      void reportOrderServiceDocumentUploadFailure({
        backendUrl,
        token,
        orderId,
        httpStatus: res.status,
        message: detail,
        filenames: [file.name],
        isAdminPath: isAdmin,
        source: isAdmin ? 'admin_service_documents' : 'client_service_documents',
      });
      throw new Error(detail);
    }
  }
}

/** Driver JWT; returns metadata for each uploaded document (append to order.service_documents). */
export async function uploadDriverOrderServiceDocuments(orderId, fileList, backendUrl, token) {
  if (!fileList || !fileList.length || !token) return [];
  const path = `${backendUrl}/api/orders/${orderId}/driver-service-documents`;
  const uploaded = [];
  for (const file of fileList) {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(path, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) {
      let detail = 'Upload failed';
      try {
        const j = await res.json();
        if (j.detail) detail = typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail);
      } catch (_) {
        /* ignore */
      }
      void reportOrderServiceDocumentUploadFailure({
        backendUrl,
        token,
        orderId,
        httpStatus: res.status,
        message: detail,
        filenames: [file.name],
        isAdminPath: false,
        source: 'driver_service_documents',
      });
      throw new Error(detail);
    }
    const body = await res.json();
    if (body.document) uploaded.push(body.document);
  }
  return uploaded;
}

export async function openServiceDocumentPreview(backendUrl, documentId, token) {
  const url = `${backendUrl}/api/service-documents/${documentId}`;
  const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) throw new Error('Could not load document');
  const blob = await r.blob();
  const objectUrl = URL.createObjectURL(blob);
  window.open(objectUrl, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(objectUrl), 120_000);
}
